import { getAIConversations } from '../../api'
import { DAY, HOUR } from '../../constants/date'
import { sleep } from '../../utils/async'

import { GretaAIChatProjectSubjectType } from './GretaAIChat.constants'

export const GretaAIChatProjectsMockResult = {
  Empty: 'empty',
  Error: 'error',
  Rows: 'rows',
}

// Switch to see the column and the projects page empty or failed.
export const GRETA_AI_CHAT_PROJECTS_MOCK_RESULT =
  GretaAIChatProjectsMockResult.Rows

export const GretaAIChatProjectMutationMockResult = {
  Error: 'error',
  Success: 'success',
}

// Switch to see the create, rename and delete errors.
export const GRETA_AI_CHAT_PROJECT_MUTATION_MOCK_RESULT =
  GretaAIChatProjectMutationMockResult.Success

const GRETA_AI_CHAT_PROJECTS_MOCK_LATENCY = 600

const GRETA_AI_CHAT_PROJECTS_MOCK_CONVERSATIONS_PARAMS = {
  archived: false,
  page: 1,
  page_size: 50,
}

const MARIAHILFER_BUILDING = {
  address: 'Mariahilfer Straße 99',
  has_access: true,
  id: 101,
  number: '101',
}

// `mock_chats_count` real conversations are taken from the top of the chat list, in order.
const GRETA_AI_CHAT_PROJECTS_MOCK = [
  {
    created_ago: 20 * DAY,
    documents_count: 3,
    id: 'mock-project-1',
    mock_chats_count: 12,
    name: 'Wasserschaden Mariahilfer 99',
    subject: {
      building: MARIAHILFER_BUILDING,
      requests: [{ id: 4821, number: '4821' }],
      type: GretaAIChatProjectSubjectType.Requests,
    },
    texts_count: 2,
  },
  {
    created_ago: 14 * DAY,
    documents_count: 1,
    id: 'mock-project-2',
    mock_chats_count: 2,
    name: 'Mariahilfer 99 — Eigentümerversammlung 2026',
    subject: {
      building: MARIAHILFER_BUILDING,
      requests: [],
      type: GretaAIChatProjectSubjectType.Building,
    },
    texts_count: 0,
  },
  {
    created_ago: 10 * DAY,
    documents_count: 0,
    id: 'mock-project-3',
    mock_chats_count: 1,
    name: 'Fassade Lindengasse',
    subject: {
      building: {
        address: 'Lindengasse 12',
        has_access: true,
        id: 102,
        number: '102',
      },
      requests: [
        { id: 4756, number: '4756' },
        { id: 4757, number: '4757' },
        { id: 4760, number: '4760' },
      ],
      type: GretaAIChatProjectSubjectType.Requests,
    },
    texts_count: 0,
  },
  {
    created_ago: 2 * HOUR,
    documents_count: 0,
    id: 'mock-project-4',
    mock_chats_count: 0,
    name: 'Heizung Gartengasse 2',
    subject: null,
    texts_count: 1,
  },
  {
    created_ago: 30 * DAY,
    documents_count: 0,
    id: 'mock-project-5',
    mock_chats_count: 1,
    name: 'Lift Hauptstraße 3',
    subject: {
      building: {
        address: 'Hauptstraße 3',
        has_access: false,
        id: 412,
        number: '412',
      },
      requests: [],
      type: GretaAIChatProjectSubjectType.Building,
    },
    texts_count: 0,
  },
  {
    created_ago: 40 * DAY,
    documents_count: 0,
    id: 'mock-project-6',
    mock_chats_count: 0,
    name: 'Betriebskostenabrechnung 2025 für alle Wohnungen der Liegenschaft Schönbrunner Straße 40',
    subject: null,
    texts_count: 0,
  },
  {
    created_ago: 45 * DAY,
    documents_count: 0,
    id: 'mock-project-7',
    mock_chats_count: 0,
    name: 'Dachsanierung',
    subject: {
      building: {
        address: 'Gartengasse 2',
        has_access: false,
        id: 204,
        number: '204',
      },
      requests: [{ id: 4702, number: '4702' }],
      type: GretaAIChatProjectSubjectType.Requests,
    },
    texts_count: 0,
  },
  {
    created_ago: 60 * DAY,
    documents_count: 0,
    id: 'mock-project-8',
    mock_chats_count: 0,
    name: 'Mieterwechsel Top 7',
    subject: null,
    texts_count: 0,
  },
]

function getGretaAIChatProjectMockActivity(createdAt, conversations) {
  return Math.max(
    createdAt,
    ...conversations.map(chat => new Date(chat.updated).getTime())
  )
}

function getGretaAIChatProjectMockChatsOffset(index) {
  return GRETA_AI_CHAT_PROJECTS_MOCK.slice(0, index).reduce(
    (offset, project) => offset + project.mock_chats_count,
    0
  )
}

function buildGretaAIChatProjectsMock(conversations) {
  return GRETA_AI_CHAT_PROJECTS_MOCK.map(
    (
      { created_ago: createdAgo, mock_chats_count: chatsCount, ...project },
      index
    ) => {
      const chatsOffset = getGretaAIChatProjectMockChatsOffset(index)
      const projectChats = conversations.slice(
        chatsOffset,
        chatsOffset + chatsCount
      )
      const createdAt = Date.now() - createdAgo

      return {
        ...project,
        conversation_ids: projectChats.map(chat => chat.id),
        created: new Date(createdAt).toISOString(),
        last_activity: new Date(
          getGretaAIChatProjectMockActivity(createdAt, projectChats)
        ).toISOString(),
      }
    }
  ).sort(
    (first, second) =>
      new Date(second.last_activity) - new Date(first.last_activity)
  )
}

async function settleGretaAIChatProjectMutationMock() {
  await sleep(GRETA_AI_CHAT_PROJECTS_MOCK_LATENCY)

  if (
    GRETA_AI_CHAT_PROJECT_MUTATION_MOCK_RESULT ===
    GretaAIChatProjectMutationMockResult.Error
  ) {
    throw new Error('Project mutation mock failure')
  }
}

// TODO: drop with the projects endpoint (IU-1835) and the project chats list (IU-1836).
export async function getMockGretaAIChatProjects({ options }) {
  const response = await getAIConversations({
    options,
    params: GRETA_AI_CHAT_PROJECTS_MOCK_CONVERSATIONS_PARAMS,
  })

  await sleep(GRETA_AI_CHAT_PROJECTS_MOCK_LATENCY)

  if (
    GRETA_AI_CHAT_PROJECTS_MOCK_RESULT === GretaAIChatProjectsMockResult.Error
  ) {
    throw new Error('Projects mock failure')
  }

  return {
    data: {
      results:
        GRETA_AI_CHAT_PROJECTS_MOCK_RESULT ===
        GretaAIChatProjectsMockResult.Empty
          ? []
          : buildGretaAIChatProjectsMock(response.data.results),
    },
  }
}

// TODO: drop the mutation mocks with the projects endpoint (IU-1835).
export async function createMockGretaAIChatProject({ name }) {
  await settleGretaAIChatProjectMutationMock()

  const now = new Date().toISOString()

  return {
    data: {
      conversation_ids: [],
      created: now,
      documents_count: 0,
      id: `mock-project-${Date.now()}`,
      last_activity: now,
      name,
      subject: null,
      texts_count: 0,
    },
  }
}

export async function renameMockGretaAIChatProject({ id, name }) {
  await settleGretaAIChatProjectMutationMock()

  return { data: { id, name } }
}

// The real delete also removes the project's chats (IU-1836); here they stay and
// return to the "Chats" section.
export async function deleteMockGretaAIChatProject({ id }) {
  await settleGretaAIChatProjectMutationMock()

  return { data: { id } }
}
